/**
 * Static utility class for date formatting
 */
export class DateFormatter {
  private static readonly DEFAULT_LOCALE = 'nb-NO';
  private static readonly DEFAULT_FALLBACK = '-';

  /**
   * Formats a date in short format (e.g., "15. jan 2025")
   * @param date Date to format (Date object, ISO string, or null)
   * @param fallback Fallback string if date is null/undefined
   * @returns Formatted date string or fallback
   */
  static formatShortDate(date: Date | string | null, fallback: string = DateFormatter.DEFAULT_FALLBACK): string {
    return this.formatDate(date, this.DEFAULT_LOCALE, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }, fallback);
  }

  /**
   * Formats a date in short format for charts (e.g., "15. jan")
   * @param date Date to format (Date object, ISO string, or null)
   * @param locale Locale to use for formatting (defaults to 'nb-NO')
   * @param fallback Fallback string if date is null/undefined
   * @returns Formatted date string or fallback
   */
  static formatShort(
    date: Date | string | null,
    locale: string = DateFormatter.DEFAULT_LOCALE,
    fallback: string = DateFormatter.DEFAULT_FALLBACK
  ): string {
    return this.formatDate(date, locale, {
      month: 'short',
      day: 'numeric',
    }, fallback);
  }

  /**
   * Formats a date in long format with full month name (e.g., "15. januar 2025")
   * @param date Date to format (Date object, ISO string, or null)
   * @param locale Locale to use for formatting (defaults to 'nb-NO')
   * @param fallback Fallback string if date is null/undefined
   * @returns Formatted date string or fallback
   */
  static formatLong(
    date: Date | string | null,
    locale: string = DateFormatter.DEFAULT_LOCALE,
    fallback: string = DateFormatter.DEFAULT_FALLBACK
  ): string {
    return this.formatDate(date, locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }, fallback);
  }

  private static formatDate(
    date: Date | string | null,
    locale: string,
    options: Intl.DateTimeFormatOptions,
    fallback: string
  ): string {
    if (!date) {
      return fallback;
    }

    const dateObj = this.toDateObject(date);
    return dateObj.toLocaleDateString(locale, options);
  }

  private static toDateObject(date: Date | string): Date {
    return typeof date === 'string' ? new Date(date) : date;
  }
}
